import classNames from 'classnames';

import { Card } from '@/components/atoms/Card';
import { Overline } from '@/components/atoms/Eyebrow';
import Tooltip, { TooltipRows } from '@/components/atoms/Tooltip';
import type { WarningTier } from '@/domain/feedingDashboard';

import {
    SurfaceQualityDot,
    type SurfaceQualityRating,
} from '../BioscopeQualityDot/BioscopeQualityDot';
import { WarningIcon } from '../WarningMarker/WarningMarker';

import { KpiCoverageNote } from './KpiCoverageNote';
import { type KpiDetail, KpiDetails } from './KpiDetails';
import { type KpiLine, KpiLineText } from './KpiLineText';

import styles from './FeedingKpiCard.module.scss';

export interface KpiWarning {
    tier: Exclude<WarningTier, 'none'>;
    /** Names the condition met, for the marker's accessible name and its tooltip. */
    label: string;
}

export interface KpiValue {
    label?: string;
    /** The figure, in the dashboard locale. */
    text: string;
    /**
     * What the figure covers, one fact per line, in a tooltip on the figure itself (U14),
     * for a fact that has no line of its own on the card to carry it.
     */
    explanation?: string[];
    /** The unit with the join before it ("\u00A0g"), set smaller than the figure. */
    unit?: string;
    /** A line under the figure that qualifies it, such as its interval (U1, U11). */
    caption?: KpiLine;
    /**
     * A pellet fallthrough band, drawn in the warning colours as the pen table does. It
     * carries no warning marker: pellet fallthrough never puts a pen in the warning state.
     */
    tone?: 'default' | 'elevated' | 'high';
    /** Set when the shared warning rule flags the value (IPS 2.2.3, statement 12). */
    warning?: KpiWarning;
}

/**
 * `quality` is the data quality dot (IPS req 11), with `qualitySubject: 'pellet'` on the
 * pellet card. Omitted for KPIs that are not Bioscope-based.
 */
export type FeedingKpiCardProps = SurfaceQualityRating & {
    label: string;
    values: KpiValue[];
    /** The card's other facts, one labelled row each. */
    details?: KpiDetail[];
    /** Which pens the values cover when fewer than all selected (IPS 5, 2.2.1), a line per value. */
    note?: string[];
    /** The pens left out and why, in the note's tooltip and accessible description. */
    noteReasons?: string[];
};

function KpiValueCell(props: { value: KpiValue }) {
    const { value } = props;
    return (
        <div className={styles.valueCell}>
            {/* Present on every value, labelled or not, so the figures of a row of cards
                share one line whether a card holds one value or two. */}
            <div className={styles.valueLabel}>
                {value.label}
                {/* The table's marker beside the label, so a touch or keyboard user can
                    learn which target the value misses, and the state is never colour
                    alone. */}
                {value.warning ? (
                    <Tooltip
                        content={<TooltipRows>{value.warning.label}</TooltipRows>}
                        placement="top"
                        layout="fit"
                        targetClassName={styles.markerAnchor}>
                        <WarningIcon label={value.warning.label} />
                    </Tooltip>
                ) : null}
            </div>
            {/* The warning state as the table draws it: a pale background and border with
                the default dark text. A div, since an explained figure's tooltip wraps it in
                one. */}
            <div
                className={classNames(styles.value, {
                    [styles.warned]: value.warning?.tier === 'warning' || value.tone === 'elevated',
                    [styles.warnedCritical]:
                        value.warning?.tier === 'critical' || value.tone === 'high',
                })}>
                {value.explanation ? (
                    <KpiLineText
                        line={{ text: value.text, explanation: value.explanation }}
                        figure
                    />
                ) : (
                    value.text
                )}
                {value.unit ? <span className={styles.unit}>{value.unit}</span> : null}
            </div>
            {value.caption ? (
                <div className={styles.caption}>
                    <KpiLineText line={value.caption} />
                </div>
            ) : null}
        </div>
    );
}

export function FeedingKpiCard(props: FeedingKpiCardProps) {
    const { label, values, details, note, noteReasons } = props;
    return (
        <Card className={styles.card} fullWidth={false}>
            <div className={styles.header}>
                <Overline>{label}</Overline>
                <SurfaceQualityDot rating={props} />
            </div>
            <div className={styles.values}>
                {values.map((value) => (
                    <KpiValueCell key={`${value.label}-${value.text}`} value={value} />
                ))}
            </div>
            {details && details.length > 0 ? <KpiDetails details={details} /> : null}
            {note && note.length > 0 ? (
                <KpiCoverageNote lines={note} reasons={noteReasons ?? []} />
            ) : null}
        </Card>
    );
}
